import clsx from "clsx";
import { ReactNode } from "react";

import { FCCC } from "@/types";

interface Props {
  /** Full-width header block — the redesign's ivory heading/icon card. */
  header: ReactNode;
  /** Full-width tab strip; the tab buttons themselves stay left-aligned. */
  tabs?: ReactNode;
  /**
   * Optional full-width block rendered between the header and the tab strip
   * (e.g. the account section's info banner).
   */
  beforeTabs?: ReactNode;
}

/**
 * Shared layout for the portal's tabbed detail pages — Rechnungen (Documents),
 * Benutzerkonto (UserAccount) and the Abo-Verwaltung (DeviceManagement).
 * Replaces the old `TabbedPageLayout`: same header/beforeTabs/tabs/children
 * slots, but the routed content now stays full width at every breakpoint
 * (no more half-width cap) and there's no positioning dependency on any
 * ancestor shell — it's a plain flex column, safe to render inside
 * `PortalPageLayout`'s content column.
 *
 * Horizontal insets and vertical rhythm follow the redesign's Figma frame: the
 * blocks are inset 12/16/24px (header/tabs/content) and separated by one
 * `gutter` step (16px mobile, 24px desktop).
 */
export const TabLayout: FCCC<Props> = ({
  header,
  tabs,
  beforeTabs,
  children,
  className,
}) => (
  <div className={clsx("flex w-full flex-col", className)}>
    {/* Figma "header-section": 12px block inset, full width. */}
    <div className="tablet:mx-3">{header}</div>

    {beforeTabs && (
      // Optional banner row, aligned to the tab strip's 16px inset.
      <div className="mt-gutter tablet:mx-4">{beforeTabs}</div>
    )}

    {/* Figma "tab-group": 16px inset, full width; the buttons stay left-aligned. */}
    {tabs && <div className="mt-gutter tablet:mx-4">{tabs}</div>}

    {/* Figma "content-section": 24px inset, full width at every breakpoint.
        min-w-0: as a flex item, this column defaults to shrink-to-fit
        (min-width: auto) — force it to 0 so wide content (tables, grids)
        handles its own overflow instead of blowing out the column width. */}
    <div className="mt-gutter min-h-[560px] min-w-0 tablet:mx-6">
      {children}
    </div>
  </div>
);
